import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, CalendarClock, MapPin, PenLine, CheckCircle2, Mail, FileDown,
  Users, Send, AlertTriangle, Loader2, BellRing, Vote, ShieldCheck, Pencil, Save, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BankIdSignButton } from "@/components/BankIdSignButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { formatDateTime, meetingTypeLabel } from "@/lib/format";
import { initials } from "./utils";

const LIFECYCLE = [
  { key: "planlagt", label: "Planlagt", icon: CalendarClock },
  { key: "innkalt", label: "Innkalt", icon: Mail },
  { key: "gjennomfort", label: "Gjennomført", icon: CheckCircle2 },
  { key: "signert", label: "Signert", icon: PenLine },
];
const LIFECYCLE_ORDER = { planlagt: 0, innkalt: 1, gjennomfort: 2, signert: 3 };

function Lifecycle({ status }) {
  const current = LIFECYCLE_ORDER[status] ?? 0;
  return (
    <div data-testid="meeting-lifecycle" className="mt-6 rounded-xl border border-border/60 bg-card p-4 shadow-sm sm:p-5">
      <div className="flex items-center">
        {LIFECYCLE.map((s, i) => {
          const done = i < current;
          const active = i === current;
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className={`grid h-9 w-9 place-items-center rounded-full border transition-colors ${
                    active
                      ? "border-transparent bg-primary text-primary-foreground shadow-sm"
                      : done
                      ? "border-transparent bg-primary/15 text-primary"
                      : "border-border bg-card text-muted-foreground/50"
                  }`}
                >
                  {done ? <CheckCircle2 className="h-4 w-4" strokeWidth={1.9} /> : <Icon className="h-4 w-4" strokeWidth={1.8} />}
                </div>
                <span className={`text-[11px] font-medium ${active || done ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
              </div>
              {i < LIFECYCLE.length - 1 ? (
                <div className={`mx-2 mb-4 h-0.5 flex-1 rounded-full transition-colors ${i < current ? "bg-primary/40" : "bg-border"}`} />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DateChip({ date }) {
  const d = new Date(date);
  const mo = d.toLocaleDateString("nb-NO", { month: "short" }).replace(".", "").toUpperCase();
  const day = d.toLocaleDateString("nb-NO", { day: "2-digit" });
  return (
    <div data-testid="meeting-date-chip" className="hidden shrink-0 overflow-hidden rounded-xl border border-border bg-card text-center shadow-sm sm:block">
      <div className="bg-primary px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-primary-foreground">{mo}</div>
      <div className="px-4 py-1.5 font-headings text-2xl font-extrabold leading-none">{day}</div>
    </div>
  );
}

function toLocalInput(v) {
  if (!v) return "";
  if (typeof v === "string" && v.length >= 16 && v[10] === "T") return v.slice(0, 16);
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function MeetingHeader({ m, meetingId, companyId, load, canManage }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [signing, setSigning] = useState(false);
  const [sendingInv, setSendingInv] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [sendingPrevote, setSendingPrevote] = useState(false);
  const [innkallingOpen, setInnkallingOpen] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!innkallingOpen) return;
    setRecipients(null);
    api.get(`/meetings/${meetingId}/innkalling-recipients`)
      .then((r) => setRecipients(r.data))
      .catch(() => setRecipients({ recipients: [], without_email: [] }));
  }, [innkallingOpen, meetingId]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", date: "", location: "" });

  const hasSigned = m.signatures?.some((s) => s.user_id === user?.id);
  const showBanner = m.status === "planlagt" && !m.innkalling_sent_at && !bannerDismissed;
  const isGf = m.type === "generalforsamling" || m.type === "ekstraordinar_gf";
  const canEditMeeting = canManage && ["planlagt", "innkalt"].includes(m.status);

  const openEdit = () => {
    setEditForm({ title: m.title || "", date: toLocalInput(m.date), location: m.location || "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.title.trim() || !editForm.date) {
      toast.error("Fyll inn tittel og dato/tid");
      return;
    }
    const dateChanged = editForm.date !== toLocalInput(m.date);
    const locChanged = (editForm.location.trim() || "Digitalt") !== (m.location || "");
    setSavingEdit(true);
    try {
      await api.put(`/meetings/${meetingId}`, {
        title: editForm.title.trim(),
        date: editForm.date,
        location: editForm.location.trim() || "Digitalt",
      });
      if (m.innkalling_sent_at && (dateChanged || locChanged)) {
        toast.success("Møtet er oppdatert. Deltakerne er varslet på e-post med oppdatert kalenderoppføring.");
      } else {
        toast.success("Møtet er oppdatert");
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const sendGfReminder = async () => {
    setSendingReminder(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/remind-gf`);
      if (data.count > 0) toast.success(`Påminnelse sendt til ${data.count} aksjonær(er)`);
      else toast.warning("Fant ingen mottakere med e-postadresse");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingReminder(false);
    }
  };

  const sendPrevoteReminder = async () => {
    setSendingPrevote(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/remind-prevote`);
      if (data.count > 0) toast.success(`Purring sendt til ${data.count} aksjonær(er) som ikke har forhåndsstemt`);
      else toast.info("Ingen å purre – alle har forhåndsstemt eller mangler e-postadresse");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingPrevote(false);
    }
  };

  const markDone = async () => {
    await api.put(`/meetings/${meetingId}`, { status: "gjennomfort" });
    toast.success("Møtet er markert som gjennomført");
    load();
  };

  const sendInnkalling = async () => {
    setSendingInv(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/send-innkalling`);
      const failed = data.failed?.length || 0;
      const total = data.count + failed;
      if (data.count > 0 && failed === 0) {
        toast.success(`Innkalling sendt til ${data.count} deltaker(e)`);
      } else if (data.count > 0) {
        toast.warning(`Innkalling sendt til ${data.count} av ${total} – ${failed} kunne ikke motta e-post`);
      } else {
        toast.error("Klarte ikke å sende innkalling til noen mottakere");
      }
      setInnkallingOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingInv(false);
    }
  };

  const sign = async () => {
    setSigning(true);
    try {
      await api.post(`/meetings/${meetingId}/sign`);
      toast.success("Signert digitalt");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  const deleteMeeting = async () => {
    setDeleting(true);
    try {
      await api.delete(`/meetings/${meetingId}`);
      toast.success("Møtet er slettet");
      navigate(`/selskap/${companyId}/styrerom`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  return (
    <>
      <Link to={`/selskap/${companyId}/styrerom`} className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Tilbake til styrerommet
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <DateChip date={m.date} />
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-headings text-2xl font-extrabold tracking-tight sm:text-3xl">{m.title}</h1>
              <StatusBadge status={m.status} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>{meetingTypeLabel(m.type)}</span>
              <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> {formatDateTime(m.date)}</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {m.location}</span>
              <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {m.attendees?.length || 0} deltagere</span>
              {canEditMeeting ? (
                <button onClick={openEdit} data-testid="edit-meeting-btn" className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors hover:bg-muted">
                  <Pencil className="h-3.5 w-3.5" /> Endre dato/tid
                </button>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="gap-2" data-testid="download-protocol-btn"
            onClick={() => openSigned(`/meetings/${meetingId}/pdf-link`).catch(() => toast.error("Kunne ikke åpne protokollen"))}>
            <FileDown className="h-4 w-4" /> Protokoll-PDF
          </Button>
          {m.bankid_signed_path ? (
            <Button variant="outline" className="gap-2" data-testid="bankid-protocol-btn"
              onClick={() => openSigned(`/signing/meeting/${meetingId}/signed-link`).catch(() => toast.error("Kunne ikke åpne det signerte dokumentet"))}>
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> BankID-signert PDF
            </Button>
          ) : null}
          {["planlagt", "innkalt"].includes(m.status) ? (
            <>
              <Button variant="outline" className="gap-2" data-testid="send-innkalling-btn" onClick={() => setInnkallingOpen(true)}>
                <Mail className="h-4 w-4" /> Send innkalling
              </Button>
              {isGf ? (
                <Button variant="outline" className="gap-2" data-testid="remind-gf-btn" onClick={sendGfReminder} disabled={sendingReminder}>
                  {sendingReminder ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Påminn aksjonærer
                </Button>
              ) : null}
              {isGf ? (
                <Button variant="outline" className="gap-2" data-testid="remind-prevote-btn" onClick={sendPrevoteReminder} disabled={sendingPrevote}>
                  {sendingPrevote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Vote className="h-4 w-4" />} Purr forhåndsstemmer
                </Button>
              ) : null}
              <Button variant="outline" onClick={markDone} data-testid="mark-done-btn">Marker gjennomført</Button>
            </>
          ) : null}
          <Button onClick={sign} disabled={signing || hasSigned} data-testid="sign-btn" className="gap-2 active:scale-[0.98]">
            <PenLine className="h-4 w-4" /> {hasSigned ? "Signert" : signing ? "Signerer …" : "Signer digitalt"}
          </Button>
          {!hasSigned ? (
            <BankIdSignButton entityType="meeting" entityId={meetingId} variant="outline" size="default" testId="bankid-meeting-btn" />
          ) : null}
          {canManage ? (
            <Button variant="outline" onClick={() => setDeleteOpen(true)} data-testid="delete-meeting-btn"
              className="gap-2 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
              <Trash2 className="h-4 w-4" /> Slett møte
            </Button>
          ) : null}
        </div>
      </div>

      {showBanner ? (
        <div data-testid="innkalling-banner" className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm">
          <span className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" /> Sakslisten er ikke sendt ut til alle deltagerne.
          </span>
          <div className="flex items-center gap-3">
            <button onClick={() => setInnkallingOpen(true)} data-testid="banner-send-innkalling" className="font-semibold text-amber-800 underline-offset-2 hover:underline dark:text-amber-200">
              Send kalenderoppføring/saksliste
            </button>
            <button onClick={() => setBannerDismissed(true)} data-testid="banner-dismiss" className="text-muted-foreground hover:text-foreground">Skjul</button>
          </div>
        </div>
      ) : null}

      <Lifecycle status={m.status} />

      <Dialog open={innkallingOpen} onOpenChange={setInnkallingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Send innkalling</DialogTitle>
            <DialogDescription>
              {isGf
                ? "Formell innkalling til generalforsamling sendes på e-post til alle aksjonærer og styremedlemmer med registrert e-postadresse. Full innkalling med saksframlegg og fullmaktsskjema følger som PDF-vedlegg."
                : "Innkalling med saksliste sendes på e-post – med PDF-vedlegg – til alle deltakere som har registrert e-postadresse."}
            </DialogDescription>
          </DialogHeader>
          {isGf ? (
            <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground" data-testid="gf-innkalling-note">
              <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>Sendes til <b className="text-foreground">alle aksjonærer</b> (ikke bare styret). Innkallingen sendes normalt minst to uker før møtet, og genereres automatisk 14 dager før dersom du ikke har sendt den selv.</span>
            </div>
          ) : null}
          <div className="rounded-lg border border-border/60 bg-muted/40 p-3">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Mottakere</p>
            <div className="mt-2 space-y-2" data-testid="innkalling-recipients">
              {recipients === null ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Henter mottakere …</div>
              ) : (recipients.recipients || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen mottakere med registrert e-postadresse.</p>
              ) : (
                (recipients.recipients || []).map((a) => (
                  <div key={a.email} className="flex items-center gap-2 text-sm">
                    <Avatar className="h-6 w-6"><AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">{initials(a.name)}</AvatarFallback></Avatar>
                    <span className="font-medium">{a.name}</span>
                    {a.role ? <span className="text-xs text-muted-foreground">· {a.role}</span> : null}
                    <span className="ml-auto truncate text-xs text-muted-foreground">{a.email}</span>
                  </div>
                ))
              )}
            </div>
            {recipients && (recipients.without_email || []).length > 0 ? (
              <div className="mt-3 rounded-md border border-amber-500/25 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="innkalling-without-email">
                <span className="font-semibold">Mangler e-post:</span> {recipients.without_email.map((w) => w.name).filter(Boolean).join(", ")} — legg til e-postadresse under Styret/roller for at de skal få innkalling.
              </div>
            ) : null}
          </div>
          {m.innkalling_sent_at ? (
            <p className="text-xs text-muted-foreground">Sist sendt {formatDateTime(m.innkalling_sent_at)}.</p>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInnkallingOpen(false)}>Avbryt</Button>
            {isGf ? (
              <Button variant="outline" className="gap-2" data-testid="preview-gf-innkalling-btn"
                onClick={() => openSigned(`/meetings/${meetingId}/gf-innkalling-pdf-link`).catch(() => toast.error("Kunne ikke åpne innkallingen"))}>
                <FileDown className="h-4 w-4" /> Forhåndsvis (PDF)
              </Button>
            ) : null}
            <Button onClick={sendInnkalling} disabled={sendingInv} className="gap-2" data-testid="confirm-innkalling-btn">
              {sendingInv ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {sendingInv ? "Sender …" : "Send innkalling"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-meeting-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" /> Endre møte</DialogTitle>
            <DialogDescription>Oppdater tittel, dato/tid og sted for møtet. Deltakere som allerede har fått innkalling bør varsles om endringen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs">Tittel</Label>
              <Input data-testid="edit-meeting-title" className="mt-1.5" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Dato og tid</Label>
              <Input data-testid="edit-meeting-date" type="datetime-local" className="mt-1.5" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Sted</Label>
              <Input data-testid="edit-meeting-location" className="mt-1.5" placeholder="Digitalt / Teams / adresse" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={savingEdit}>Avbryt</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="gap-2" data-testid="edit-meeting-save">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteOpen} onOpenChange={(o) => !deleting && setDeleteOpen(o)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-meeting-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-destructive" /> Slett møte</DialogTitle>
            <DialogDescription>
              Er du sikker på at du vil slette «{m.title}»? Møtet fjernes permanent, inkludert saksliste{isGf ? ", forhåndsstemmer" : ""} og eventuelle oppgaver knyttet til møtet. Dette kan ikke angres.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>Avbryt</Button>
            <Button variant="destructive" onClick={deleteMeeting} disabled={deleting} className="gap-2" data-testid="confirm-delete-meeting-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {deleting ? "Sletter …" : "Slett møte"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
